import { useFieldArray } from 'react-hook-form';
import { Field, FieldRow } from '../../../forms/fields';
import type { SectionProps } from '../sectionProps';

/**
 * HOUSEHOLD section — people & birth years (drives retirement age, plan horizon, Thai RMF age-55).
 * A `useFieldArray` over `household.people`. This section is implemented as the WORKED EXAMPLE of
 * the section pattern (register + field array + inline errors); the other sections follow it.
 */
export function HouseholdSection({ form }: SectionProps): React.ReactElement {
  const { register, control, formState } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'household.people' });
  const errors = formState.errors.household?.people;

  return (
    <section>
      <h2>Household</h2>
      <p style={{ color: 'var(--ink-muted)', marginBottom: 'var(--sp-6)' }}>
        People in the plan. Birth year drives retirement age, the horizon, and RMF eligibility.
      </p>

      {fields.map((f, i) => (
        <div key={f.id} className="card" style={{ marginBottom: 'var(--sp-4)' }}>
          <FieldRow>
            <div style={{ flex: 2, minWidth: 180 }}>
              <Field label="Name" error={errors?.[i]?.name}>
                <input className="input" {...register(`household.people.${i}.name`)} />
              </Field>
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <Field label="Birth year" error={errors?.[i]?.birthYear}>
                <input
                  className="input input--num"
                  type="number"
                  {...register(`household.people.${i}.birthYear`, { valueAsNumber: true })}
                />
              </Field>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 'var(--sp-4)' }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => remove(i)}
                disabled={fields.length <= 1}
                aria-label="Remove person"
              >
                Remove
              </button>
            </div>
          </FieldRow>
        </div>
      ))}

      <button
        type="button"
        className="btn"
        onClick={() =>
          append({
            id: `p${fields.length + 1}`,
            name: '',
            birthYear: new Date().getFullYear() - 30,
          })
        }
      >
        + Add person
      </button>
    </section>
  );
}
